/**
 * Derive Getting Started step completion from Platform Overview dashboard payload
 * (`GET /platform-analytics/dashboard`).
 */

import { GETTING_STARTED_STEPS } from '../data/gettingStartedConfig';

function moduleById(dashboard) {
  const map = {};
  for (const mod of dashboard?.modules || []) {
    if (mod?.id) map[mod.id] = mod;
  }
  return map;
}

function metricSum(mod, ...labels) {
  if (!mod?.metrics?.length) return Number(mod?.primary_metric) || 0;
  if (!labels.length) {
    return (mod.metrics || []).reduce((sum, m) => sum + (Number(m.value) || 0), 0);
  }
  const wanted = new Set(labels.map((l) => l.toLowerCase()));
  return (mod.metrics || []).reduce((sum, m) => {
    if (wanted.has(String(m.label || '').toLowerCase())) {
      return sum + (Number(m.value) || 0);
    }
    return sum;
  }, 0);
}

function primary(mod) {
  return Number(mod?.primary_metric) || 0;
}

/**
 * @param {object|null|undefined} dashboard — `/platform-analytics/dashboard` body
 * @returns {{
 *   completed: Record<string, boolean>,
 *   completedCount: number,
 *   total: number,
 *   activeStepIndex: number,
 *   allComplete: boolean,
 * }}
 */
export function deriveGettingStartedProgress(dashboard) {
  const mods = moduleById(dashboard);
  const summary = dashboard?.summary || {};

  const websites = Number(summary.websites) || primary(mods.websites) || 0;

  const leadJobs = metricSum(mods.lead_scraper, 'Scrape jobs') || primary(mods.lead_scraper);
  const leadsFound = metricSum(mods.lead_scraper, 'Leads found');
  const crmLeads = metricSum(mods.marketing_crm, 'Leads');
  const crmContacts = metricSum(mods.marketing_crm, 'Contacts');
  const hasLeads = leadJobs > 0 || leadsFound > 0 || crmLeads > 0 || crmContacts > 0;

  const emailCampaigns = metricSum(mods.marketing_email, 'Campaigns');
  const emailsSent = metricSum(mods.marketing_email, 'Emails sent');
  const smsSent = metricSum(mods.marketing_sms, 'SMS sent');
  const waSent = metricSum(mods.marketing_whatsapp, 'Broadcasts sent');
  const waContacts = metricSum(mods.marketing_whatsapp, 'Contacts');
  const outreach = metricSum(mods.automations, 'Outreach events');
  const hasReach =
    emailCampaigns > 0 ||
    emailsSent > 0 ||
    smsSent > 0 ||
    waSent > 0 ||
    waContacts > 0 ||
    outreach > 0;

  const cofounderCompanies = metricSum(mods.ai_cofounder, 'Companies') || primary(mods.ai_cofounder);
  const cofounderSessions = metricSum(mods.ai_cofounder, 'Sessions');
  const hasCofounder = cofounderCompanies > 0 || cofounderSessions > 0;

  const connections = primary(mods.connections);
  const hasApiKeys = Boolean(summary.has_api_keys);
  const hasUnlock = hasApiKeys || connections > 0;

  const completed = {
    'launch-site': websites > 0,
    'find-leads': hasLeads,
    'reach-customers': hasReach,
    'ai-cofounder': hasCofounder,
    'unlock-platform': hasUnlock,
  };

  const total = GETTING_STARTED_STEPS.length;
  const completedCount = GETTING_STARTED_STEPS.filter((s) => completed[s.id]).length;
  let activeStepIndex = GETTING_STARTED_STEPS.findIndex((s) => !completed[s.id]);
  if (activeStepIndex < 0) activeStepIndex = total - 1; // all done — keep last selected-looking, no auto-dismiss

  return {
    completed,
    completedCount,
    total,
    activeStepIndex,
    allComplete: completedCount === total,
  };
}
